import { type Maybe, hasValue, isUUID } from '@vrplatform/utils';
import type {
  activeStatus_enum,
  automation_bool_exp,
  automation_template_bool_exp,
} from '@vrplatform/graphql';

export const whereAutomations = ({
  tenantId,
  isPartnerAdmin,
  isSuperAdmin,
  search,
  status,
  accountingConnectionId,
  templateType,
  isVrpAdmin,
  scheduleStatus,
  connectionId,
}: {
  search?: Maybe<string>;
  status?: Maybe<activeStatus_enum>;
  scheduleStatus?: Maybe<activeStatus_enum>;
  tenantId: string;
  accountingConnectionId?: string;
  connectionId?: Maybe<string>;
  templateType?: (
    | 'ownerStatements.ownerStatements'
    | 'ownerStatements.ownerStatementBills'
    | 'commission.managementCommission'
  )[];
} & WhereAutomationTemplates): automation_bool_exp => {
  const _and: automation_bool_exp['_and'] = [];

  if (accountingConnectionId && isUUID(accountingConnectionId)) {
    _and.push({
      _or: [
        {
          rightConnection: {
            app: { category: { _neq: 'accountingPlatform' } },
          },
          leftConnection: {
            app: { category: { _neq: 'accountingPlatform' } },
          },
        },
        {
          leftConnectionId: {
            _eq: accountingConnectionId,
          },
        },
        {
          rightConnectionId: {
            _eq: accountingConnectionId,
          },
        },
      ],
    });
  }

  if (connectionId) {
    _and.push({
      _or: [
        {
          leftConnectionId: {
            _eq: connectionId,
          },
        },
        {
          rightConnectionId: {
            _eq: connectionId,
          },
        },
      ],
    });
  }

  return {
    tenantId: { _eq: tenantId },
    status: status ? { _eq: status } : { _neq: 'inactive' },
    scheduleStatus: scheduleStatus ? { _eq: scheduleStatus } : undefined,
    ttemplate: {
      visibility: whereAutomationTemplateVisibility({
        isPartnerAdmin,
        isSuperAdmin,
        isVrpAdmin,
      }),
      type: templateType?.length ? { _in: templateType } : undefined,
    },
    _or: search
      ? [
          { title: { _ilike: `%${search}%` } },
          { ttemplate: { title: { _ilike: `%${search}%` } } },
        ]
      : undefined,
    _and,
  };
};

type WhereAutomationTemplates = {
  isSuperAdmin: boolean;
  isPartnerAdmin: boolean;
  isVrpAdmin: boolean;
};

export const whereAutomationTemplateVisibility = ({
  isPartnerAdmin,
  isSuperAdmin,
  isVrpAdmin,
}: WhereAutomationTemplates): automation_template_bool_exp['visibility'] => ({
  _in: [
    isPartnerAdmin ? 'partnerAdmin' : undefined,
    isSuperAdmin || isVrpAdmin ? 'admin' : undefined,
    'public',
  ].filter(hasValue),
});
